import { GetTaskByIdDocument, useDeleteSubtaskMutation } from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogProps,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';

import { useQSetReviewTask } from '../../providers';

export interface ConfirmRemoveReviewerModalProps extends Omit<DialogProps, 'onSubmit' | 'open'> {
  onClose: () => void;
  open?: boolean;
  subtaskId: string;
}

export default function ConfirmRemoveReviewerModal(
  props: Readonly<ConfirmRemoveReviewerModalProps>,
) {
  const { onClose, open = false, subtaskId } = props;

  const { mainTaskDetail } = useQSetReviewTask();
  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const [deleteSubtask, { loading: isSubmitting }] = useDeleteSubtaskMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Reviewer has been removed', variant: 'success' });
      onClose();
    },
    onError: (error) => {
      logger.error(error);
      showSnackbar({ message: 'Failed to remove reviewer', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId: mainTaskDetail.id,
          },
        },
      },
    ],
  });

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    deleteSubtask({
      variables: {
        deleteSubtaskInput: {
          subtaskId,
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <DialogTitle onClose={handleClose}>Remove Reviewer</DialogTitle>
      <DialogContent>คุณแน่ใจหรือไม่ว่าต้องการลบผู้รีวิวออกจากชุดข้อสอบนี้</DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} loading={isSubmitting}>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
